import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {noop, Observable, Subject, Subscription} from 'rxjs';
import {AlertTicket, MessageTranslateService, Ticket, TicketSearchParams, TicketsService, TicketStatusId, TicketType} from '@shared/services';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AceSelectComponent} from '@shared/components/ace-select/ace-select.component';
import {debounceTime, filter, finalize, map, takeUntil, tap} from 'rxjs/operators';
import {TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {CompareWithFn} from '@ng-select/ng-select/lib/ng-select.component';
import {I18nMessage} from '@shared/api/model/i18n';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {Params} from '@angular/router';
import {TicketPagedModel} from '@shared/services/api/tickets/tickets-api-client';
import {Task, TaskId} from '@shared/services/api/task.interface';
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {ArrayUtils} from '@shared/util/collections/collection-utils';

type TicketSelectValue = Ticket | Ticket[] | null;

export interface TicketClearableResult {
  nonClearable: boolean;
  reason?: I18nMessage;
}


@Component({
  selector: 'app-ticket-select',
  templateUrl: './ticket-select.component.html',
  styleUrls: ['./ticket-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TicketSelectComponent),
    multi: true,
  }],
  standalone: false
})
export class TicketSelectComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {

  private readonly DEFAULT_TICKET_ORDER_BY: string[] = ['status', 'updated_at DESC'];
  private readonly MINIMUM_SEARCH_TERM_LENGTH = 3;
  private readonly TICKET_FETCH_PAGE_SIZE = 50;
  private readonly TYPEAHEAD_DEBOUNCE_TIME = 500;

  TicketType = TicketType;
  TicketStatusId = TicketStatusId;
  ticketSearchLoading = false;

  @Input() site: SiteInfo | null;
  @Input() ticketsLoading = false;
  @Input() inEditMode = false;
  @Input() canChangeEditMode = true;
  @Input() ticketType: TicketType;
  @Input() orderBy: string[] = this.DEFAULT_TICKET_ORDER_BY;
  @Input() multiSelect = false;
  @Input() hideSelected = true;
  @Input() allowToOpenTicketView = true;
  @Input() clearable = false;
  @Input() saving = false;
  @Input() placeholder: string;
  @Input() appendTo: string;

  @Input() canClearSelectionFn: (ticket: Ticket) => TicketClearableResult;

  @Output() ticketsLoaded: EventEmitter<Map<string, Ticket>> = new EventEmitter();
  @Output() touched: EventEmitter<any> = new EventEmitter();
  @Output() openEvent: EventEmitter<any> = new EventEmitter();
  @Output() closeEvent: EventEmitter<any> = new EventEmitter();
  @Output() saveEvent: EventEmitter<void> = new EventEmitter();
  @Output() cancelEvent: EventEmitter<void> = new EventEmitter();
  @Output() viewTicket: EventEmitter<Ticket> = new EventEmitter();

  @ViewChild('ticketsSelect', { static: false }) ticketsSelect: AceSelectComponent;


  // tickets fetched in the later pages could potentially be fetched before then updated in the backend
  // use map here to dedupe the tickets.
  private ticketsById: Map<string, Ticket> = new Map();
  private allTicketsFetched = false;
  private currentOffset = 0;
  private currentSearchTerm: string;
  private currentTypeaheadValue: string;
  private getTicketsSubscription: Subscription;
  private onChange: (value: TicketSelectValue) => void = noop;
  private onTouched: () => void = noop;

  private readonly logger: AceLogger;
  private readonly ngUnsubscribe: Subject<void> = new Subject();

  formControl = new FormControl<TicketSelectValue>(null);
  typeahead = new Subject<string>();
  availableTickets: Ticket[];
  tasksLoading = false;
  tasksById = new Map<TaskId, Task>();

  constructor(private ticketsService: TicketsService,
              private translateService: MessageTranslateService,
              private ticketDataCaches: TicketDataCaches,
              logService: AceLoggerService) {
    this.logger = logService.create(TicketSelectComponent.name);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.verifyTicketType();
    this.setupTypeaheadSearch();
    this.setupValueChangeHandler();
    this.typeahead.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(typeaheadValue => this.currentTypeaheadValue = typeaheadValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ticketType) {
      this.verifyTicketType();
    }
    if (changes.orderBy && this.orderBy == null) {
      this.orderBy = this.DEFAULT_TICKET_ORDER_BY;
    }
    if (changes.site) {
      this.availableTickets =  null;
    }
    if (changes.ticketsLoading && !this.ticketsLoading && this.currentTypeaheadValue?.trim()) {
      this.typeahead.next(this.currentTypeaheadValue);
    }
  }

  registerOnChange(fn: (value: TicketSelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: TicketSelectValue): void {
    const existingValue = this.formControl.value;
    if (existingValue == null && value == null) {
      return;
    }

    const valueIsArray = Array.isArray(value);
    if (value != null && ((this.multiSelect && !valueIsArray) || (!this.multiSelect && valueIsArray))) {
      throw new Error('Value "' + value + '" does not match multiSelect value of "' + !!this.multiSelect);
    }

    const existingValueIsArray = Array.isArray(existingValue);
    let valueChanged: boolean;
    if (existingValueIsArray !== valueIsArray) {
      valueChanged = true;
    } else {
      const getId = (ticket: Ticket): string => ticket?.ticket_id ?? null;
      const asArray = (v: TicketSelectValue): Ticket[] => v == null ? [] : Array.isArray(v) ? v : [v];

      const arr1: string[] = asArray(this.formControl.value).map(getId);
      const arr2: string[] = asArray(value).map(getId);
      valueChanged = !ArrayUtils.containSameItems(arr1, arr2);
    }

    if (valueChanged) {
      this.formControl.setValue(value, { emitEvent: false });
      this.checkAndReloadTasksForValue(value);
    }
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formControl.disable({ emitEvent: false }) : this.formControl.enable({ emitEvent: false });
  }

  private checkAndReloadTasksForValue(value: TicketSelectValue): void {
    if (this.ticketType === TicketType.ALERT) {
      const alerts = Array.isArray(value) ? value : value != null ? [value] : [];
      this.loadTasks(alerts.map((alert: AlertTicket) => alert.rule_id));
    } else {
      this.tasksById = null;
    }
  }

  private verifyTicketType(): void {
    if (this.ticketType == null) {
      throw new Error("ticketType property is required.");
    }
  }

  private setupValueChangeHandler(): void {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: TicketSelectValue) => this.onChange(value));
  }

  private checkAndCancelTicketSearch(): void {
    if (this.getTicketsSubscription != null) {
      this.getTicketsSubscription.unsubscribe();
      this.getTicketsSubscription = null;
    }
  }

  private setupTypeaheadSearch(): void {
    this.typeahead.pipe(
      takeUntil(this.ngUnsubscribe),
      tap(() => this.checkAndCancelTicketSearch()),
      debounceTime(this.TYPEAHEAD_DEBOUNCE_TIME),
      map((searchTerm: string | null) => searchTerm?.trim().toLowerCase()),
      filter((searchTerm: string | null) => searchTerm?.length >= this.MINIMUM_SEARCH_TERM_LENGTH))
      .subscribe((searchTerm: string) =>  {
        this.updateTicketSearchTerm(searchTerm);
        if (!this.ticketsLoading) {
          this.searchTickets({ summary_web_search: searchTerm });
        }
    });
  }

  private updateTicketSearchTerm(searchTerm: string): void {
    this.currentOffset = 0;
    this.allTicketsFetched = false;
    this.currentSearchTerm = searchTerm;
    this.ticketsById.clear();
  }

  private searchTickets(searchParams?: TicketSearchParams): void {
    this.ticketSearchLoading = true;
    const params: TicketSearchParams = {
      ...searchParams,
      ...this.site != null ? { site_id: this.site.site_id } : null,
      ticket_archived: false
    };
    const getTickets$: Observable<TicketPagedModel<Ticket[]>> = this.ticketType === TicketType.ESCALATED
        ? this.ticketsService.getEscalatedTickets(params, this.TICKET_FETCH_PAGE_SIZE, this.currentOffset, this.orderBy, {includeLinkedTickets: true})
        : this.ticketsService.getAlertTickets(params, this.TICKET_FETCH_PAGE_SIZE, this.currentOffset, this.orderBy, {includeLinkedTickets: true});
    this.getTicketsSubscription = getTickets$
      .pipe(finalize(() => this.ticketSearchLoading = false))
      .subscribe((tickets) => this.processTicketsResult(tickets));
  }

  private loadTasks(taskIds: number[]): void {
    if (taskIds?.length) {
      this.tasksLoading = true;
      this.ticketDataCaches.getTasksByIds(taskIds)
        .pipe(finalize(() => this.tasksLoading = false))
        .subscribe(tasksById => tasksById.forEach((task, taskId) => this.tasksById.set(taskId, task)));
    }
  }

  private reloadOptions(): void {
    let availableTickets: Ticket[] = Array.from(this.ticketsById.values());
    // it's possible that tickets fetched in the later page already appeared in the earlier page with a different status
    // so here we always sort the tickets again before displaying.
    TicketUtils.sortTickets(availableTickets, this.orderBy || this.DEFAULT_TICKET_ORDER_BY);
    this.availableTickets = availableTickets;
  }

  private processTicketsResult(tickets: TicketPagedModel<Ticket[]> | null) {
    tickets?.data?.forEach(t => TicketUtils.registerTicketInMap(t, this.ticketsById, true));
    this.currentOffset += tickets?.data?.length || 0;
    this.allTicketsFetched = this.currentOffset >= (tickets?.count || 0);
    this.reloadOptions();
    this.ticketsLoaded.emit(this.ticketsById);
  }

  onScrollToEnd(): void {
    if (!this.allTicketsFetched && this.currentSearchTerm?.length >= this.MINIMUM_SEARCH_TERM_LENGTH) {
      this.searchTickets({ summary_web_search: this.currentSearchTerm });
    }
  }

  onSelectTouched(): void {
    this.onTouched();
    if (!this.multiSelect && this.canChangeEditMode) {
      this.setEditable(false);
    }
    this.touched.emit();
  }

  onOpen(): void {
    this.openEvent.emit();
  }

  onClose(): void {
    this.closeEvent.emit();
  }

  onClear(): void {
    this.onSelectTouched();
  }

  setEditable(editable: boolean): void {
    this.inEditMode = editable;
  }

  cancelEdit(): void {
    this.setEditable(false);
    this.cancelEvent.emit();
  }

  onSaveClicked(): void {
    this.checkAndReloadTasksForValue(this.formControl.value);
    this.saveEvent.emit();
  }

  canViewTicket(): boolean {
    return this.allowToOpenTicketView && !this.inEditMode && !this.saving;
  }

  onViewTicket(ticket: Ticket, event: MouseEvent): void {
    if (this.canViewTicket()) {
      event.stopPropagation();
      this.viewTicket.emit(ticket);
    }
  }

  getTicketTypeDisplayText(ticketType: TicketType, plural: boolean): string {
    const typeNameI18NMessage = this.ticketsService.getTicketTypeName(ticketType, plural);
    return this.translateService.instant(typeNameI18NMessage.messageKey);
  }

  getChartLinkParamsForTicket(ticket: Ticket): Params | null {
    if (ticket.type === TicketType.ALERT) {
      const alertTicket = <AlertTicket>ticket;
      const ruleId = alertTicket.rule_id;
      const task = this.tasksById?.get(ruleId);
      if (task) {
        try {
          return this.ticketsService.getChartLinkParamsForAlertTask(task, this.site, alertTicket.last_fault_transitioned_at);
        } catch (error) {
          this.logger.error(
              'Failed to generate chart link params for alert with id ' + alertTicket.ticket_id
              + ' and rule id ' + ruleId + ', error:' + error);
        }
      }
    }
    return null;
  }

  compareFn: CompareWithFn = (ticket1: Ticket, ticket2: Ticket) => ticket1?.ticket_id === ticket2?.ticket_id;

}
